import axios from 'axios';

export interface CompanySettings {
  id: number;
  name: string;
  address: string;
  phone: string;
  email: string;
  logo?: string;
  favicon?: string;
  logo_shape?: 'square' | 'circle';
  logo_zoom?: number;
  theme?: string;
}

export interface CompanyFormData {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  theme?: string;
  logo_shape?: 'square' | 'circle';
  logo_zoom?: number;
}

export interface EmailSettings {
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_password: string;
  from_name: string;
  from_email: string;
  isConfigured: boolean;
}

class CompanyService {
  async getCompanySettings(): Promise<CompanySettings> {
    console.log('🏢 CompanyService: Getting company settings');
    try {
      const response = await axios.get('/company/settings');
      console.log('✅ CompanyService: Company settings retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CompanyService: Error getting company settings', error.response?.data || error.message);
      throw error;
    }
  }

  async updateCompanySettings(data: CompanyFormData): Promise<CompanySettings> {
    console.log('✏️ CompanyService: Updating company settings', data);
    try {
      const response = await axios.put('/company/settings', data);
      console.log('✅ CompanyService: Company settings updated', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CompanyService: Error updating company settings', error.response?.data || error.message);
      throw error;
    }
  }

  async uploadLogo(file: File): Promise<string> {
    console.log('🖼️ CompanyService: Uploading logo', { name: file.name, size: file.size });
    try {
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await axios.post('/company/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('✅ CompanyService: Logo uploaded', response.data);
      return response.data.data.logoUrl;
    } catch (error: any) {
      console.log('❌ CompanyService: Error uploading logo', error.response?.data || error.message);
      throw error;
    }
  }

  async uploadFavicon(file: File): Promise<string> {
    console.log('🔗 CompanyService: Uploading favicon', { name: file.name, size: file.size });
    try {
      const formData = new FormData();
      formData.append('favicon', file);
      
      const response = await axios.post('/company/favicon', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('✅ CompanyService: Favicon uploaded', response.data);
      return response.data.data.faviconUrl;
    } catch (error: any) {
      console.log('❌ CompanyService: Error uploading favicon', error.response?.data || error.message);
      throw error;
    }
  }

  async getEmailSettings(): Promise<EmailSettings> {
    console.log('📧 CompanyService: Getting email settings');
    try {
      const response = await axios.get('/company/email');
      console.log('✅ CompanyService: Email settings retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CompanyService: Error getting email settings', error.response?.data || error.message);
      throw error;
    }
  }

  async updateEmailSettings(data: Omit<EmailSettings, 'isConfigured'>): Promise<EmailSettings> {
    console.log('✏️ CompanyService: Updating email settings', { ...data, smtp_password: '***' });
    try {
      const response = await axios.put('/company/email', data);
      console.log('✅ CompanyService: Email settings updated', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ CompanyService: Error updating email settings', error.response?.data || error.message);
      throw error;
    }
  }

  async testEmailSettings(data: { testEmail: string }): Promise<void> {
    console.log('🧪 CompanyService: Testing email settings', data);
    try {
      const response = await axios.post('/company/email/test', data);
      console.log('✅ CompanyService: Email test successful', response.data);
    } catch (error: any) {
      console.log('❌ CompanyService: Error testing email settings', error.response?.data || error.message);
      throw error;
    }
  }

  // Helper method to get full URL for uploaded files
  getFileUrl(path: string): string {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3001';
    return `${baseUrl}${path}`;
  }

  // Method to update document title
  updateDocumentTitle(companyName: string): void {
    document.title = `${companyName} - Sistema Empresarial`;
  }

  // Method to update favicon in document
  updateFavicon(faviconUrl: string): void {
    if (!faviconUrl) return;
    
    const fullUrl = this.getFileUrl(faviconUrl);
    
    // Remove existing favicon links
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach(link => link.remove());
    
    // Add new favicon
    const link = document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = fullUrl;
    document.getElementsByTagName('head')[0].appendChild(link);
  }

  // Validation helpers
  validateCompanyData(data: CompanyFormData): string[] {
    const errors: string[] = [];

    if (data.name !== undefined && (!data.name || data.name.trim().length < 2)) {
      errors.push('El nombre de la empresa debe tener al menos 2 caracteres');
    }

    if (data.email !== undefined && (!data.email || !this.isValidEmail(data.email))) {
      errors.push('El email debe ser válido');
    }

    if (data.phone !== undefined && (!data.phone || data.phone.trim().length < 5)) {
      errors.push('El teléfono debe tener al menos 5 caracteres');
    }

    if (data.address !== undefined && (!data.address || data.address.trim().length < 5)) {
      errors.push('La dirección debe tener al menos 5 caracteres');
    }

    return errors;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  validateImageFile(file: File, maxSize: number = 5 * 1024 * 1024): string[] {
    const errors: string[] = [];

    if (!file.type.startsWith('image/')) {
      errors.push('El archivo debe ser una imagen');
    }

    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      errors.push(`El archivo no debe superar ${maxSizeMB}MB`);
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/x-icon'];
    if (!allowedTypes.includes(file.type)) {
      errors.push('Formato de imagen no permitido. Use JPG, PNG, GIF, WebP o ICO');
    }

    return errors;
  }

  // Format company information for display
  formatCompanyInfo(company: CompanySettings): {
    displayName: string;
    contactInfo: string;
    logoUrl: string;
    faviconUrl: string;
    logoShape: string;
    logoZoom: number;
  } {
    return {
      displayName: company.name || 'Mi Empresa',
      contactInfo: [company.phone, company.email].filter(Boolean).join(' • '),
      logoUrl: this.getFileUrl(company.logo || ''),
      faviconUrl: this.getFileUrl(company.favicon || ''),
      logoShape: company.logo_shape || 'square',
      logoZoom: company.logo_zoom || 100
    };
  }
}

export const companyService = new CompanyService();